import { StrictMode, useEffect, useRef } from "react";

import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import { BannerProvider } from "@/components/banner";
import { Toaster } from "@/components/ui/sonner";
import {
  BILLING_BLOCKED_EVENT,
  isBillingBlockedError,
  shouldRetryQuery,
} from "@/queries/api/apiUtils";
import { invalidateBillingStatus } from "@/queries/hooks/billingHooks";
import { env, notify } from "@/utils";
import {
  SUBSCRIPTION_BILLING_ENABLED,
  isBillingResolvePath,
  isSetupPath,
} from "@/utils/billing";
import { Logger, consoleSink } from "@/utils/logger";

import {
  capturePageview,
  identifyUser,
  initAnalytics,
  resetUser,
} from "@/lib/analytics/posthog";

import { AppErrorBoundary } from "./components/ErrorBoundary.tsx";
import { AuthProvider, useAuth } from "./context/auth.tsx";
import "./i18n";
import { useLanguageSync } from "./i18n/useLanguageSync.ts";
import { routeTree } from "./routeTree.gen.ts";

Logger.use(consoleSink(env.DEV ? "trace" : "info"));

// After a deploy, a tab opened on the previous build still references the old hashed
// route chunks, which the deploy deleted (CloudFront then serves index.html instead), so
// lazy-loading a page fails and the route shows "Something went wrong loading this page".
// Reload once to pick up the new build. The timestamp guard stops a reload loop if the
// chunk is genuinely broken.
const CHUNK_RELOAD_KEY = "pm:chunk-reload-at";
window.addEventListener("vite:preloadError", (event) => {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - last < 10_000) return;
  event.preventDefault();
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

// Initialize analytics once at bootstrap. No-ops cleanly when no key is set.
initAnalytics();

// Global listeners for runtime errors
window.addEventListener("error", (e) => {
  try {
    Logger.error("window.error", {
      message: e.message,
      source: (e as any).filename,
      line: (e as any).lineno,
      col: (e as any).colno,
    });
  } catch {
    // Fallback to console if logging fails (avoid invoking logger sinks that may be instrumented)

    try {
      console.error("window.error", {
        message: e.message,
        source: (e as any).filename,
        line: (e as any).lineno,
        col: (e as any).colno,
      });
    } catch {}
  }
});
window.addEventListener("unhandledrejection", (e) => {
  try {
    Logger.error("unhandledrejection", {
      reason: (e.reason && (e.reason.message ?? String(e.reason))) || "unknown",
    });
  } catch {
    try {
      console.error("unhandledrejection", {
        reason:
          (e.reason && (e.reason.message ?? String(e.reason))) || "unknown",
      });
    } catch {}
  }
});

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Mutations that surface their own errors opt out of the global toast.
      if (mutation.meta?.suppressErrorToast === true) return;
      // 402 BILLING_BLOCKED → the billing listener below redirects instead.
      if (SUBSCRIPTION_BILLING_ENABLED && isBillingBlockedError(error)) return;
      const msg = (error as any)?.message || "Something went wrong";
      notify.error(msg);
    },
  }),
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
  },
});

// This code is only for TypeScript
declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

if (env.DEV) {
  // This code is for all users
  window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  context: { auth: undefined, queryClient },
});
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "updated" && event.query.getObserversCount() === 0) {
    const err = event.query.state.error;
    if (err) {
      Logger.warn("RQ query error (unobserved)", {
        queryKey: event.query.queryKey,
        message: err?.message,
      });
    }
  }
});

// Subscription billing: any API call answered with HTTP 402 BILLING_BLOCKED
// (apiUtils emits BILLING_BLOCKED_EVENT) → refresh billing status and send the
// owner to the resolution page.
if (SUBSCRIPTION_BILLING_ENABLED) {
  window.addEventListener(BILLING_BLOCKED_EVENT, () => {
    void invalidateBillingStatus(queryClient);
    const { pathname } = router.state.location;
    if (isBillingResolvePath(pathname) || isSetupPath(pathname)) return;
    void router.navigate({
      to: "/app/$role/billing/resolve",
      // Only owner endpoints return BILLING_BLOCKED.
      params: { role: "employer" },
    });
  });
}

// Fire a manual $pageview on every resolved navigation (SPA pageviews).
router.subscribe("onResolved", () => {
  capturePageview(window.location.href);
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function Router() {
  const auth = useAuth();
  useLanguageSync();

  // Identify on login, reset on logout. Re-identify when the user id changes.
  const lastIdentified = useRef<string | null>(null);
  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      if (lastIdentified.current !== auth.user.userId) {
        identifyUser({
          pm_user_id: auth.user.userId,
          email: auth.user.email,
          // `role` is the client's view of the flow the user is in; the
          // authoritative user_type is set server-side (T3).
          user_type: auth.user.role,
        });
        lastIdentified.current = auth.user.userId;
      }
    } else if (lastIdentified.current !== null) {
      resetUser();
      lastIdentified.current = null;
    }
  }, [auth.isAuthenticated, auth.user]);

  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

function App() {
  return (
    <StrictMode>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BannerProvider>
            <AuthProvider>
              <Router />
            </AuthProvider>
            <Toaster />
          </BannerProvider>
        </QueryClientProvider>
      </AppErrorBoundary>
    </StrictMode>
  );
}
// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
